import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Copy } from "lucide-react";
import PageMeta from "@/components/PageMeta";

export default function Contact() {
  const [c, setC] = useState(null);
  const [f, setF] = useState({ name: "", email: "", phone: "", category: "General", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/company").then(r => setC(r.data)); }, []);
  const hq = c?.headquarters;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email: f.email });
      await api.post("/preorders", { name: f.name, email: f.email, phone: f.phone, product_id: "contact", notes: `[${f.category}] ${f.message}` }).catch(() => {});
      toast.success("Message received. Our concierge will contact you shortly.");
      setF({ name: "", email: "", phone: "", category: "General", message: "" });
    } catch { toast.error("Please try again."); }
    setBusy(false);
  };

  const copyAddr = () => {
    if (!hq) return;
    navigator.clipboard.writeText([hq.name, ...hq.address_lines].join("\n"));
    toast.success("Address copied");
  };

  return (
    <div className="bg-ivory text-ink pt-32 pb-24 min-h-screen">
      <PageMeta title="Contact · The LOOXS Concierge" description="Reach the LOOXS concierge for orders, appointments, media, careers and partnerships. Customer care care@looxsfashion.com · careers careers@looxsfashion.com · office of the CEO ceo@looxsfashion.com." />
      <div className="max-w-[1200px] mx-auto px-8 md:px-16">
        <div className="text-center mb-16">
          <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-6">Concierge</p>
          <h1 className="font-editorial text-5xl md:text-7xl leading-[0.95] tracking-tight">Contact LOOXS</h1>
          <div className="rule-gold w-32 mx-auto my-10" />
          <p className="max-w-2xl mx-auto text-ink/70 leading-relaxed">A private conversation with our concierge — for orders, appointments, media, careers, or partnership.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* HQ */}
          <div className="p-10" style={{ background: "#f4ecd8" }}>
            <p className="text-[10px] tracking-widest-plus uppercase text-goldDark mb-4">Headquarters</p>
            <h2 className="font-editorial text-3xl leading-tight">{hq?.name}</h2>
            <div className="rule-gold w-12 my-6" />
            <ul className="space-y-2 text-ink/80">
              {hq?.address_lines?.map((l, i) => <li key={i} className="flex gap-3 items-start">{i === 0 && <MapPin className="w-4 h-4 mt-1 text-goldDark" strokeWidth={1.25} />}<span className={i === 0 ? "" : "ml-7"}>{l}</span></li>)}
            </ul>
            <button data-testid="copy-address" onClick={copyAddr} className="mt-6 flex items-center gap-2 text-[11px] tracking-widest-plus uppercase link-draw">
              <Copy className="w-3 h-3" strokeWidth={1.25} /> Copy address
            </button>

            <div className="mt-10 pt-8 border-t border-ink/10 space-y-4">
              <p className="text-[10px] tracking-widest-plus uppercase text-goldDark">Direct lines</p>
              <div>
                <p className="text-[10px] tracking-widest-plus uppercase text-ink/50">Customer care</p>
                <a data-testid="email-care" href="mailto:care@looxsfashion.com" className="font-editorial text-lg link-draw">care@looxsfashion.com</a>
              </div>
              <div>
                <p className="text-[10px] tracking-widest-plus uppercase text-ink/50">Careers</p>
                <a data-testid="email-careers" href="mailto:careers@looxsfashion.com" className="font-editorial text-lg link-draw">careers@looxsfashion.com</a>
              </div>
              <div>
                <p className="text-[10px] tracking-widest-plus uppercase text-ink/50">Office of the CEO</p>
                <a data-testid="email-ceo" href="mailto:ceo@looxsfashion.com" className="font-editorial text-lg link-draw">ceo@looxsfashion.com</a>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <label className="block col-span-2">
                <span className="text-[10px] tracking-widest-plus uppercase text-ink/50">Name</span>
                <input required data-testid="contact-name" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} className="w-full border-b border-ink/25 focus:border-goldDark py-2 outline-none bg-transparent" />
              </label>
              <label className="block">
                <span className="text-[10px] tracking-widest-plus uppercase text-ink/50">Email</span>
                <input required type="email" data-testid="contact-email" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} className="w-full border-b border-ink/25 focus:border-goldDark py-2 outline-none bg-transparent" />
              </label>
              <label className="block">
                <span className="text-[10px] tracking-widest-plus uppercase text-ink/50">Phone</span>
                <input data-testid="contact-phone" value={f.phone} onChange={(e)=>setF({...f,phone:e.target.value})} className="w-full border-b border-ink/25 focus:border-goldDark py-2 outline-none bg-transparent" />
              </label>
              <label className="block col-span-2">
                <span className="text-[10px] tracking-widest-plus uppercase text-ink/50">Enquiry type</span>
                <select value={f.category} onChange={(e)=>setF({...f,category:e.target.value})} className="w-full border-b border-ink/25 focus:border-goldDark py-2 outline-none bg-transparent text-ink">
                  {["General","Order","Careers","Press / Media","Partnership"].map(x => <option key={x}>{x}</option>)}
                </select>
              </label>
              <label className="block col-span-2">
                <span className="text-[10px] tracking-widest-plus uppercase text-ink/50">Message</span>
                <textarea required rows="4" data-testid="contact-message" value={f.message} onChange={(e)=>setF({...f,message:e.target.value})} className="w-full border-b border-ink/25 focus:border-goldDark py-2 outline-none bg-transparent resize-none" />
              </label>
            </div>
            <button data-testid="contact-submit" disabled={busy} className="w-full bg-ink text-ivory py-4 text-[11px] tracking-widest-plus uppercase hover:bg-goldDark transition-colors duration-500 disabled:opacity-40">
              {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
